/**
 * Job listing: checkboxes (job type, sector, location), refresh on change, clear all, pagination.
 * URL kept in sync; fallback to full page load on fetch failure.
 */
function initListingAjax() {
  const section = document.querySelector('.listing-section[data-listing-fragment-url]')
  const form = document.getElementById('listing-filters-form')
  const resultsEl = document.getElementById('listing-results')
  const clearBtn = document.getElementById('listing-clear-filters')
  const filtersToggle = document.getElementById('listing-filters-toggle')
  const filtersWrap = section?.querySelector('.listing-filters-toggle-wrap')
  const filtersPanel = document.getElementById('listing-filters-panel')
  const paginationWrap = document.getElementById('listing-pagination-wrap')

  if (!section || !form || !resultsEl) return

  // On initial load, move pagination from inside #listing-results to #listing-pagination-wrap
  if (paginationWrap) {
    const navInside = resultsEl.querySelector('.listing-pagination')
    if (navInside) {
      paginationWrap.appendChild(navInside)
    }
  }

  const fragmentPathRaw = section.getAttribute('data-listing-fragment-url') || '/jobs/listing'
  const isAbsoluteUrl = /^https?:\/\//i.test(fragmentPathRaw)
  const listingPath = window.location.pathname.replace(/\/$/, '') || '/'

  function buildQueryString(params) {
    const search = new URLSearchParams()
    const values = (arr) => (Array.isArray(arr) ? arr.filter(Boolean) : arr ? [arr] : [])
    const locationVals = values(params.location)
    const sectorVals = values(params.sector)
    const jobTypeVals = values(params.jobType)
    const keywordsVal = (params.keywords && String(params.keywords).trim()) || ''
    const pageNum = params.page && params.page !== '1' ? params.page : null
    if (pageNum) search.set('page', pageNum)
    if (locationVals.length) search.set('location', locationVals.join(','))
    if (sectorVals.length) search.set('sector', sectorVals.join(','))
    if (jobTypeVals.length) search.set('jobType', jobTypeVals.join(','))
    if (keywordsVal) search.set('keywords', keywordsVal)
    const q = search.toString()
    return q || ''
  }

  function getFormParams() {
    const formData = new FormData(form)
    const keywordsInput = form.querySelector('input[name="keywords"]')
    const keywords = keywordsInput ? (keywordsInput.value || '').trim() : (formData.get('keywords') || '').trim()
    return {
      page: '1',
      location: formData.getAll('location[]').filter(Boolean),
      sector: formData.getAll('sector[]').filter(Boolean),
      jobType: formData.getAll('jobType[]').filter(Boolean),
      keywords: keywords
    }
  }

  function getParamsFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const sectorParam = params.get('sector')
    const jobTypeParam = params.get('jobType')
    const keywordsParam = params.get('keywords') || ''
    return {
      page: params.get('page') || '1',
      location: (params.get('location') || '').split(',').map((s) => s.trim()).filter(Boolean),
      sector: sectorParam ? sectorParam.split(',').map((s) => s.trim()).filter(Boolean) : [],
      jobType: jobTypeParam ? jobTypeParam.split(',').map((s) => s.trim()).filter(Boolean) : [],
      keywords: keywordsParam.trim()
    }
  }

  function syncFormFromParams(params) {
    const normalize = (v) => (Array.isArray(v) ? v : (v ? [v] : []))
    const locations = normalize(params.location)
    const sectors = normalize(params.sector)
    const jobTypes = normalize(params.jobType)
    form.querySelectorAll('input[name="location[]"]').forEach((cb) => { cb.checked = locations.includes(cb.value) })
    form.querySelectorAll('input[name="sector[]"]').forEach((cb) => { cb.checked = sectors.includes(cb.value) })
    form.querySelectorAll('input[name="jobType[]"]').forEach((cb) => { cb.checked = jobTypes.includes(cb.value) })
    const keywordsInput = form.querySelector('input[name="keywords"]')
    if (keywordsInput && params.keywords !== undefined) keywordsInput.value = params.keywords || ''
  }

  function updateHistory(params) {
    const query = buildQueryString(params)
    const fullUrl = query ? `${listingPath}?${query}` : listingPath
    window.history.pushState({ listingParams: params }, '', fullUrl)
  }

  function showListingError() {
    resultsEl.innerHTML = '<p class="latest-vacancies__empty h5">Unable to load results. Please try again or refresh the page.</p>'
    if (paginationWrap) paginationWrap.innerHTML = ''
    resultsEl.removeAttribute('aria-busy')
  }

  function refreshResults(resetPage = true) {
    const params = getFormParams()
    if (resetPage) params.page = '1'
    const pageInput = form.querySelector('[data-page-input]')
    if (pageInput) pageInput.value = params.page
    updateHistory(params)
    fetchAndReplace(params, { skipHistory: true })
  }

  function fetchAndReplace(params, options = {}) {
    const { skipHistory = false, scrollToTop = false } = options
    const query = buildQueryString(params)
    const fragmentUrl = isAbsoluteUrl
      ? `${fragmentPathRaw.replace(/\?.*$/, '')}${query ? `?${query}` : ''}`
      : `${window.location.origin}${fragmentPathRaw.startsWith('/') ? fragmentPathRaw : `/${fragmentPathRaw}`}${query ? `?${query}` : ''}`

    resultsEl.setAttribute('aria-busy', 'true')

    fetch(fragmentUrl, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Listing request failed: ${res.status}`)
        return res.text()
      })
      .then((html) => {
        if (typeof html !== 'string' || html.length === 0) throw new Error('Empty response')
        if (paginationWrap) {
          const doc = new DOMParser().parseFromString(html, 'text/html')
          const resultsList = doc.querySelector('.listing-results-list')
          const paginationNav = doc.querySelector('.listing-pagination')
          resultsEl.innerHTML = resultsList ? resultsList.outerHTML : html
          paginationWrap.innerHTML = paginationNav ? paginationNav.outerHTML : ''
        } else {
          resultsEl.innerHTML = html
        }
        resultsEl.removeAttribute('aria-busy')
        updateListingCount()
        if (!skipHistory) updateHistory(params)
        reattachListingListeners()
        setTimeout(() => {
          if (scrollToTop) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              })
            })
          }
        }, 200);
      })
      .catch(() => {
        showListingError()
      })
  }

  function updateListingCount() {
    const wrapper = resultsEl.querySelector('[data-total]')
    const totalEl = document.getElementById('listing-total')
    if (wrapper && totalEl) totalEl.textContent = wrapper.getAttribute('data-total') || '0'
  }

  function reattachListingListeners() {
    const paginationLinks = section.querySelectorAll('.listing-pagination__link[data-listing-page-link], .listing-pagination__link[href*="page"]')
    paginationLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const page = link.getAttribute('data-listing-page-link') || (() => {
          try { return new URL(link.href, window.location.origin).searchParams.get('page') } catch (_) { return null }
        })()
        const params = { ...getFormParams(), page: String(page || '1') }
        const pageInput = form.querySelector('[data-page-input]')
        if (pageInput) pageInput.value = page || '1'
        fetchAndReplace(params, { scrollToTop: true })
      })
    })
  }

  function attachFilterListeners() {
    form.querySelectorAll('.listing-filters__checkbox').forEach((cb) => {
      cb.addEventListener('change', () => refreshResults(true))
    })

    // Keyword: update on blur or after user stops typing (debounced)
    const keywordsInput = form.querySelector('input[data-listing-keywords], input[name="keywords"]')
    if (keywordsInput) {
      let keywordDebounceTimer = null
      const debounceMs = 400

      function runKeywordSearch() {
        if (keywordDebounceTimer) {
          clearTimeout(keywordDebounceTimer)
          keywordDebounceTimer = null
        }
        refreshResults(true)
      }

      keywordsInput.addEventListener('input', () => {
        if (keywordDebounceTimer) clearTimeout(keywordDebounceTimer)
        keywordDebounceTimer = setTimeout(runKeywordSearch, debounceMs)
      })
      keywordsInput.addEventListener('blur', () => {
        if (keywordDebounceTimer) {
          clearTimeout(keywordDebounceTimer)
          keywordDebounceTimer = null
        }
        refreshResults(true)
      })
    }
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      form.querySelectorAll('.listing-filters__checkbox').forEach((cb) => { cb.checked = false })
      const pageInput = form.querySelector('[data-page-input]')
      if (pageInput) pageInput.value = '1'
      refreshResults(true)
    })
  }

  if (filtersToggle && filtersWrap) {
    const toggleTextEl = filtersToggle.querySelector('.listing-filters-toggle-btn__text')
    filtersToggle.addEventListener('click', () => {
      const isOpen = filtersWrap.classList.toggle('is-open')
      filtersToggle.setAttribute('aria-expanded', isOpen)
      if (toggleTextEl) toggleTextEl.textContent = isOpen ? 'Hide filters' : 'Show filters'
    })
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    refreshResults(true)
  })

  window.addEventListener('popstate', (e) => {
    if (e.state && e.state.listingParams) {
      syncFormFromParams(e.state.listingParams)
      fetchAndReplace(e.state.listingParams, { skipHistory: true })
    } else if (window.location.pathname.replace(/\/$/, '') === listingPath) {
      const params = getParamsFromUrl()
      syncFormFromParams(params)
      fetchAndReplace(params, { skipHistory: true })
    }
  })

  updateListingCount()
  attachFilterListeners()
  reattachListingListeners()
}

export default initListingAjax
